import React, { useEffect, useState } from "react";

import { useAuth } from "../../hooks/useAuth";
import { authService } from "../../services/auth.service";
import styles from "./ConfirmEmailBanner.module.css";

/**
 * Quiet reminder for an account whose email is not confirmed yet.
 *
 * Sign-up lets people straight in, so this is the only place the email step
 * shows up, and it never blocks anything. When the session allowance runs out
 * the API refuses a new recording and this bar becomes the explanation, in the
 * same slot where clinicians already look for app-level notices.
 */
export const ConfirmEmailBanner: React.FC = () => {
  const { user, refreshProfile } = useAuth();
  const [required, setRequired] = useState(false);
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const [error, setError] = useState<string | null>(null);

  const unconfirmed = user?.isVerified === false;

  useEffect(() => {
    const onRequired = () => setRequired(true);
    window.addEventListener("email-confirmation:required", onRequired);
    return () => window.removeEventListener("email-confirmation:required", onRequired);
  }, []);

  // The link is usually opened in another tab. Coming back to this one is when
  // the bar should clear itself.
  useEffect(() => {
    if (!unconfirmed) return;
    const onFocus = () => void refreshProfile();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [unconfirmed, refreshProfile]);

  if (!unconfirmed || !user) return null;

  // Nothing comes before the first note: five sessions run unconfirmed, so nothing is lost.
  if (user.firstRun && !required) return null;

  const resend = async () => {
    setState("sending");
    setError(null);
    try {
      await authService.resendVerificationEmail(user.email);
      setState("sent");
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "We could not send it. Please try again.");
      setState("idle");
    }
  };

  return (
    <div
      role={required ? "alert" : "status"}
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "4px 12px",
        padding: "8px 16px",
        fontSize: 14,
        background: required ? "#fef2f2" : "#eff4ff",
        color: required ? "#7f1d1d" : "#00359e",
        borderBottom: `1px solid ${required ? "#fecaca" : "#d1e0ff"}`,
      }}
    >
      <span style={{ fontWeight: required ? 600 : 500 }}>
        <span className={styles.long}>
          {required
            ? "Please confirm your email to keep recording."
            : "Please confirm your email when you can."}{" "}
          We sent a link to{" "}
        </span>
        <span className={styles.short}>Please confirm </span>
        {user.email}
        <span className={styles.long}>.</span>
        {required && <span className={styles.short}> to keep recording</span>}
      </span>
      {state === "sent" ? (
        <span style={{ fontWeight: 600 }}>Sent. Please check your inbox and spam folder.</span>
      ) : (
        <button
          type="button"
          onClick={() => void resend()}
          disabled={state === "sending"}
          style={{
            padding: 0,
            border: 0,
            background: "none",
            color: "inherit",
            font: "inherit",
            fontWeight: 600,
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          {state === "sending" ? "Sending…" : "Send the link again"}
        </button>
      )}
      {error && <span>{error}</span>}
    </div>
  );
};
